import { Button } from "@goodfill/ui-components";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { route } from "../../../../routes";
import { canKillOrFillOrder } from "../../../order/canKillOrFillOrder";
import { canModifyOrder } from "../../../order/canModifyOrder";
import { OrderKillOrFillButton } from "../../../order/OrderKillOrFillButton";
import { BondPriceComponent, FaceAmountComponent } from "@goodfill/domain-properties-components";
import { CorporateBondId, OrderTicketId, asAmount, OrderDirection } from "@goodfill/domain-properties";
import { PresentedOrderTicket } from "./PresentedOrderTicket";
import { sortByKeys, SortColumn } from "@goodfill/sort";
import { getOrderTicketComparator, PresentedOrderTicketForSort } from "./OrderTicketComparator";
import DataGrid, { Column } from "react-data-grid";
import AutoSizer from "react-virtualized-auto-sizer";
import { mapSortColumnFromGrid } from "../../../../sort/mapSortColumnFromGrid";
import { defaultOrderFilterConstraints, orderFilters, OrderTicketFilter } from "./OrderTicketFilter";
import { GridTableFilterRow } from "../../../../ui-components/GridTable/GridTableFilterRow";
import { MaturityDateColumnDefinition } from "../../../DataGridFilteredColumns/bond/MaturityDateColumnDefinition";
import { applyFilters } from "../../../../filters/filters";
import { FilterSimpleSelectBox } from "../../../../filters/FilterSimpleSelectBox";
import { getSortedIsins, IsinColumnDefinition } from "../../../DataGridFilteredColumns/bond/IsinColumnDefinition";
import {
  getSortedIssuerIds,
  IssuerIdColumnDefinition,
} from "../../../DataGridFilteredColumns/bond/IssuerIdColumnDefinition";
import {
  CouponPaymentColumnDefinition,
  getSortedBondCouponPayments,
} from "../../../DataGridFilteredColumns/bond/CouponPaymentColumnDefinition";
import { AlreadyFilledAmountColumnDefinition } from "../../../DataGridFilteredColumns/order/AlreadyFilledAmountColumnDefinition";
import { MoodysRatingColumnDefinition } from "../../../DataGridFilteredColumns/bond/MoodysRatingColumnDefinition";
import {
  getSortedPaymentRanks,
  PaymentRankColumnDefinition,
} from "../../../DataGridFilteredColumns/bond/PaymentRankColumnDefinition";
import { SpRatingColumnDefinition } from "../../../DataGridFilteredColumns/bond/SpRatingColumnDefinition";
import {
  getSortedTickers,
  TickerColumnDefinition,
} from "../../../DataGridFilteredColumns/bond/TickerColumnDefinition";
import { CreatedAtDateColumnDefinition } from "../../../DataGridFilteredColumns/order/CreatedAtDateColumnDefinition";
import { ExpiresAtDateColumnDefinition } from "../../../DataGridFilteredColumns/order/ExpiresDateColumnDefinition";
import { FaceAmountColumnDefinition } from "../../../DataGridFilteredColumns/order/FaceAmountColumnDefinition";
import { OrderStatusColumnDefinition } from "../../../DataGridFilteredColumns/order/OrderStatusColumnDefinition";
import {
  getSortedOrderTicketIds,
  OrderTicketIdColumnDefinition,
} from "../../../DataGridFilteredColumns/order/OrderTicketIdColumnDefinition";
import { TimeInForceColumnDefinition } from "../../../DataGridFilteredColumns/order/TimeInForceColumnDefinition";
import { OrderBalanceAmountColumnDefinition } from "../../../DataGridFilteredColumns/order/OrderBalanceAmountColumnDefinition";
import { PostFeePriceColumnDefinition } from "../../../DataGridFilteredColumns/order/PostFeePriceColumnDefinition";

type SortableKeys = keyof PresentedOrderTicketForSort;

export const OrderListTable = ({
  orderTicketRows,
  reload,
}: { orderTicketRows: PresentedOrderTicket[]; reload: () => void }) => {
  const navigate = useNavigate();
  const [sortColumns, setSortColumns] = useState<readonly SortColumn<SortableKeys>[]>([
    { columnKey: "createdAt", direction: "DESC" },
  ]);

  const onClickDetail = useCallback(
    (bondId: CorporateBondId, orderId: OrderTicketId) => () => {
      navigate(route.orderReceipt(bondId, orderId));
    },
    [navigate],
  );

  const onClickModify = useCallback(
    (bondId: CorporateBondId, orderId: OrderTicketId) => () => {
      navigate(route.orderModify(bondId, orderId));
    },
    [navigate],
  );

  const [criteria, setCriteria] = useState(defaultOrderFilterConstraints);

  const onFilterChange = <T extends keyof OrderTicketFilter>(key: T, value: OrderTicketFilter[T]) =>
    setCriteria({ ...criteria, [key]: value });

  const filteredRows = applyFilters(orderTicketRows, orderFilters, criteria);

  // TODO: abstract sortedRows/summaryRows into useSortedRows(rows) and useSummaryRows(rows), respectively
  const sortedRows = useMemo(
    () => sortByKeys(filteredRows, sortColumns, getOrderTicketComparator),
    [filteredRows, sortColumns],
  );

  const tableDefinition: Array<
    Column<PresentedOrderTicket> & {
      key: keyof PresentedOrderTicket | "_detail" | "_fillOrKill" | "_modify" | "balanceAmount";
    }
  > = useMemo(() => {
    const issuerIds = getSortedIssuerIds("issuerId", orderTicketRows);
    const isins = getSortedIsins("bondId", orderTicketRows);
    const tickers = getSortedTickers("ticker", orderTicketRows);
    const paymentRanks = getSortedPaymentRanks("bondPaymentRank", orderTicketRows);
    const orderTicketIds = getSortedOrderTicketIds("id", orderTicketRows);
    const bondCouponPayment = getSortedBondCouponPayments("bondCouponPayment", orderTicketRows);

    return [
      {
        key: "_detail",
        name: "",
        formatter: ({ row }) => (
          <Button fill size="small" uppercase={true} primary={true} onClick={onClickDetail(row.bondId, row.id)}>
            detail
          </Button>
        ),
        width: 110,
      },
      {
        key: "_fillOrKill",
        name: "",
        formatter: ({ row }) =>
          canKillOrFillOrder(row) ? <OrderKillOrFillButton orderId={row.id} onSuccess={reload} /> : <></>,
        width: 110,
      },
      {
        key: "_modify",
        name: "",
        formatter: ({ row }) =>
          canModifyOrder(row) ? (
            <Button fill size="small" uppercase={true} green={true} onClick={onClickModify(row.bondId, row.id)}>
              modify
            </Button>
          ) : (
            <></>
          ),
        width: 110,
      },
      CreatedAtDateColumnDefinition("createdAt", criteria, onFilterChange),
      ExpiresAtDateColumnDefinition("expiresAt", criteria, onFilterChange),
      OrderTicketIdColumnDefinition("id", criteria, onFilterChange, orderTicketIds),
      OrderStatusColumnDefinition("status", criteria, onFilterChange),
      TimeInForceColumnDefinition("timeInForce", criteria, onFilterChange),
      TickerColumnDefinition("ticker", criteria, onFilterChange, tickers),
      IssuerIdColumnDefinition("issuerId", criteria, onFilterChange, issuerIds),
      PaymentRankColumnDefinition("bondPaymentRank", criteria, onFilterChange, paymentRanks),
      IsinColumnDefinition("bondId", criteria, onFilterChange, isins),
      CouponPaymentColumnDefinition("bondCouponPayment", criteria, onFilterChange, bondCouponPayment),
      MaturityDateColumnDefinition("bondMaturityDate", criteria, onFilterChange),
      {
        key: "direction",
        name: "Buy/Sell",
        sortable: true,
        formatter: ({ row }) => <>{row.direction}</>,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <FilterSimpleSelectBox
              small
              options={[OrderDirection.Buy, OrderDirection.Sell]}
              value={`${criteria.direction}`}
              onChange={(value) => onFilterChange("direction", value !== null ? (value as OrderDirection) : null)}
            />
          </GridTableFilterRow>
        ),
        width: 120,
      },
      FaceAmountColumnDefinition("faceAmount", criteria, onFilterChange),
      AlreadyFilledAmountColumnDefinition("alreadyFilled", criteria, onFilterChange),
      OrderBalanceAmountColumnDefinition("balanceAmount", criteria, onFilterChange),
      PostFeePriceColumnDefinition("loadedBondPrice", criteria, onFilterChange),
      MoodysRatingColumnDefinition("bondRatingMoodys", criteria, onFilterChange),
      SpRatingColumnDefinition("bondRatingSp", criteria, onFilterChange),
    ];
  }, [reload, onClickModify, onClickDetail, criteria, onFilterChange, orderTicketRows]);

  const isDark = false;

  return (
    <AutoSizer disableHeight>
      {({ height, width }) => (
        <DataGrid
          className={`rdg-copyable ${isDark ? "rdg-dark" : "rdg-light"} OrderListTable custom-scrollbar`}
          columns={tableDefinition}
          rows={sortedRows}
          headerRowHeight={75}
          sortColumns={sortColumns}
          onSortColumnsChange={(columns) => setSortColumns(columns.map(mapSortColumnFromGrid<SortableKeys>))}
          style={{ height: 700, width }}
        />
      )}
    </AutoSizer>
  );
};
